import { formatISO, parseISO } from 'date-fns';
import { cn } from './lib/utils';
import { Calendar } from './Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import Icon from '../Icon/Icon';
import { Button } from './Button';
import { useEffect, useState } from 'react';
import Label from '../Form/Label/Label';
import TimePicker from './TimePicker';
import { utcToZonedTime, format as tzFormat } from 'date-fns-tz';

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function DatePicker({
  granularity = 'day',
  name,
  label,
  onChange,
  placeholder,
  disabled,
  className,
  value,
}: {
  granularity?: 'day' | 'minute';
  name?: string;
  label?: string;
  onChange: (date: string, name?: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  value?: string; // Unix timestamp in string format
}) {
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState('');
  const [dateTimeValue, setDateTimeValue] = useState('');

  useEffect(() => {
    if (value) {
      // Parse the Unix timestamp (string) to a Date object
      const timestamp = parseInt(value, 10) * 1000; // Convert to milliseconds
      const parsedDate = new Date(timestamp);

      if (!isNaN(parsedDate.getTime())) {
        setDate(parsedDate);
        if (granularity === 'minute') {
          // Extract time in 'HH:mm' format
          const hours = parsedDate.getHours().toString().padStart(2, '0');
          const minutes = parsedDate.getMinutes().toString().padStart(2, '0');
          setTime(`${hours}:${minutes}`);
        }
      }
    }
  }, [value, granularity]);

  useEffect(() => {
    const effectiveTimezone = getUserTimeZone();

    if (!date) {
      setDateTimeValue('');
      return;
    }

    let isoDateTime;
    let readableDateTime;

    try {
      if (granularity === 'minute' && time) {
        isoDateTime = `${formatISO(date, { representation: 'date' })}T${time}`;
        const parsedDateTime = parseISO(isoDateTime);
        const zonedDate = utcToZonedTime(parsedDateTime, effectiveTimezone);

        // Check if zonedDate is valid
        if (!isNaN(zonedDate.getTime())) {
          readableDateTime = tzFormat(zonedDate, 'PPpp', { timeZone: effectiveTimezone });
          setDateTimeValue(readableDateTime);
          onChange(isoDateTime, name);
        }
      } else {
        const zonedDate = utcToZonedTime(date, effectiveTimezone);

        // Check if zonedDate is valid
        if (!isNaN(zonedDate.getTime())) {
          readableDateTime = tzFormat(zonedDate, 'PP', { timeZone: effectiveTimezone });
          isoDateTime = formatISO(zonedDate);
          setDateTimeValue(readableDateTime);
          onChange(isoDateTime, name);
        }
      }
    } catch (error) {
      console.error('Error processing date/time:', error);
    }
  }, [date, time, granularity, name]);

  return (
    <>
      {label && <Label text={label} />}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={'outline'}
            className={cn(
              'w-[280px] justify-start text-left font-normal !border-ui-200 !rounded max-h-[34px]',
              !date && 'text-ui',
              className ? className : ''
            )}
            disabled={disabled}
          >
            <Icon name='calendar' className='w-4 h-4 mr-2' />
            {date
              ? granularity === 'minute' && time
                ? dateTimeValue
                : formatISO(date, { representation: 'date' })
              : placeholder || 'Pick a date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0 bg-white'>
          <Calendar
            mode='single'
            selected={date}
            defaultMonth={date || new Date()}
            onSelect={(newDate) => setDate(newDate || undefined)}
          />
          <div className='flex justify-center'>
            {granularity === 'minute' && <TimePicker onChange={(time) => setTime(time)} />}
          </div>
        </PopoverContent>
      </Popover>
      <input type='hidden' name={name} value={dateTimeValue} />
    </>
  );
}
